import { format } from 'date-fns'
import { Download } from 'lucide-react'
import type { SlotMetrics, TopLink } from '@/api/hooks/use-campaign-analytics'
import { useAnalyticsExport, useCampaignSlots } from '@/api/hooks/use-campaign-analytics'
import { DataTable } from '@/components/ui/data-table'
import { MetricCard } from '@/components/ui/metric-card'
import { StatusBadge } from '@/components/ui/status-badge'

function pct(n: number, d: number): string {
  if (!d) return '0.00%'
  return `${((n / d) * 100).toFixed(2)}%`
}

interface CampaignClicksTabProps {
  campaignId: string
  campaignCreatedAt: string
}

export function CampaignClicksTab({
  campaignId,
  campaignCreatedAt,
}: CampaignClicksTabProps): React.JSX.Element {
  const { data: slotData, isLoading } = useCampaignSlots(campaignId)
  const exportMutation = useAnalyticsExport()

  const totalSent = slotData?.total_sent ?? 0
  const slots = slotData?.slots ?? []
  const topLinks = slotData?.top_links ?? []
  const totalClicks = slots.reduce((s, r) => s + (r.clicks ?? 0), 0)
  const totalUnique = slots.reduce((s, r) => s + (r.unique_clicks ?? 0), 0)

  function handleExport(fmt: 'csv' | 'pdf'): void {
    exportMutation.mutate({
      type: 'campaign',
      campaign_id: campaignId,
      from: format(new Date(campaignCreatedAt), 'yyyy-MM-dd'),
      to: format(new Date(), 'yyyy-MM-dd'),
      format: fmt,
    })
  }

  if (isLoading) {
    return <p className="py-8 text-center text-sm text-text-tertiary">Loading click analytics...</p>
  }

  if (!slotData) {
    return (
      <p className="py-8 text-center text-sm text-text-tertiary">
        No click analytics available for this campaign.
      </p>
    )
  }

  return (
    <div className="space-y-6">
      {/* Export buttons */}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={() => handleExport('csv')}
          disabled={exportMutation.isPending}
          className="inline-flex items-center gap-1.5 rounded-lg border border-surface-3 px-3 py-2 text-sm font-medium text-text-primary hover:bg-surface-1 disabled:opacity-50"
        >
          <Download className="h-4 w-4" />
          {exportMutation.isPending ? 'Exporting...' : 'CSV'}
        </button>
        <button
          type="button"
          onClick={() => handleExport('pdf')}
          disabled={exportMutation.isPending}
          className="inline-flex items-center gap-1.5 rounded-lg border border-surface-3 px-3 py-2 text-sm font-medium text-text-primary hover:bg-surface-1 disabled:opacity-50"
        >
          <Download className="h-4 w-4" />
          PDF
        </button>
      </div>

      {/* Metric cards */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <MetricCard label="Sent" value={totalSent.toLocaleString()} />
        <MetricCard label="Total Clicks" value={totalClicks.toLocaleString()} />
        <MetricCard label="Unique Clicks" value={totalUnique.toLocaleString()} />
        <MetricCard label="CTR" value={pct(totalUnique, totalSent)} />
      </div>

      {/* Slot performance */}
      <div>
        <h3 className="mb-3 text-sm font-medium text-text-secondary">Slot Performance</h3>
        {slots.length === 0 ? (
          <p className="py-6 text-center text-sm text-text-tertiary">
            No click data yet for this campaign.
          </p>
        ) : (
          <DataTable
            columns={[
              {
                key: 'slot',
                label: 'Slot',
                render: (r: SlotMetrics) => <span className="font-medium">{r.slot}</span>,
              },
              {
                key: 'type',
                label: 'Type',
                render: (r: SlotMetrics) => (
                  <StatusBadge status={r.type === 'ad' ? 'pending' : 'active'}>
                    {r.type}
                  </StatusBadge>
                ),
              },
              {
                key: 'clicks',
                label: 'Clicks',
                render: (r: SlotMetrics) => (r.clicks ?? 0).toLocaleString(),
              },
              {
                key: 'unique_clicks',
                label: 'Unique',
                render: (r: SlotMetrics) => (r.unique_clicks ?? 0).toLocaleString(),
              },
              {
                key: 'ctr',
                label: 'CTR',
                render: (r: SlotMetrics) => `${(r.ctr ?? 0).toFixed(2)}%`,
              },
            ]}
            data={slots}
          />
        )}
      </div>

      {/* Top clicked links */}
      <div>
        <h3 className="mb-3 text-sm font-medium text-text-secondary">Top Clicked Links</h3>
        {topLinks.length === 0 ? (
          <p className="py-6 text-center text-sm text-text-tertiary">
            No link clicks recorded yet.
          </p>
        ) : (
          <DataTable
            columns={[
              {
                key: 'url',
                label: 'URL',
                render: (r: TopLink) => (
                  <a
                    href={r.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="max-w-xs truncate text-brand-500 hover:underline"
                  >
                    {r.url}
                  </a>
                ),
              },
              { key: 'slot', label: 'Slot', render: (r: TopLink) => r.slot },
              {
                key: 'clicks',
                label: 'Clicks',
                render: (r: TopLink) => (r.clicks ?? 0).toLocaleString(),
              },
              {
                key: 'unique_clicks',
                label: 'Unique',
                render: (r: TopLink) => (r.unique_clicks ?? 0).toLocaleString(),
              },
            ]}
            data={topLinks}
          />
        )}
      </div>
    </div>
  )
}
